/**
 * §10 Research trees — Phase 2.F.1.
 *
 * MVP cut: one tree (Industrial Progress) with 8 nodes laid out as a small DAG
 * with two branches that converge. The full §10 spec (~7 trees, 50-100 nodes
 * per tree) is intentionally NOT delivered here — Phase 2.F.1 ships the
 * mechanics (timer, slots, prereqs, costs, completion ledger) so subsequent
 * phases can scale the catalog without re-touching the schema or the routes.
 *
 * Pure-TS, server-authoritative. Effects strings are descriptive only at this
 * cut; consumers (production speed, march speed, slot count) read the player's
 * completed-node set directly when their formulas land in later phases.
 */
import type { BuildingFamily as _BuildingFamily } from "./buildings.js";

export const RESEARCH_TREE_IDS = ["INDUSTRIAL_PROGRESS"] as const;
export type ResearchTreeId = (typeof RESEARCH_TREE_IDS)[number];

export interface ResearchTree {
  readonly id: ResearchTreeId;
  readonly displayName: string;
  /** OKLCH hue used as the tree accent across HUD surfaces (info popup, panel, hex menu). */
  readonly accentHue: number;
  /** Original-prose blurb shown in the tree header. */
  readonly lore: string;
}

export const RESEARCH_TREES: Record<ResearchTreeId, ResearchTree> = {
  INDUSTRIAL_PROGRESS: {
    id: "INDUSTRIAL_PROGRESS",
    displayName: "Industrial Progress",
    accentHue: 60,
    lore:
      "Factory throughput, raw-material yield, and assembly-line cadence. Every node here makes the rest of your war effort cheaper to keep moving.",
  },
};

/**
 * Per-node definition. Keys are stable strings (UPPERCASE_SNAKE) — these flow
 * through the wire into the `research_jobs.node_id` and `player_research.node_id`
 * columns. Adding a node never changes existing keys.
 */
export interface ResearchNode {
  readonly id: string;
  readonly tree: ResearchTreeId;
  readonly displayName: string;
  /** Tier 1 = root; tier N has at least one prereq from tier N-1 (or N-2/N-3 for joining branches). */
  readonly tier: 1 | 2 | 3 | 4 | 5;
  readonly prereqs: readonly string[];
  /** Treasury cost to start the research. */
  readonly costTreasury: number;
  /** Reserves cost; 0 for nodes that don't draw on stockpile reserves. */
  readonly costReserves: number;
  /** Wall-clock duration in milliseconds. Server uses this as the deadline. */
  readonly durationMs: number;
  /** One-line description of what the player gets when this node is complete. */
  readonly effect: string;
}

export const RESEARCH_NODES: ReadonlyArray<ResearchNode> = [
  {
    id: "INDUSTRIAL_BASICS",
    tree: "INDUSTRIAL_PROGRESS",
    displayName: "Industrial Basics",
    tier: 1,
    prereqs: [],
    costTreasury: 500,
    costReserves: 0,
    durationMs: 30_000,
    effect: "+5% factory output rate.",
  },
  {
    id: "STEEL_MILLING",
    tree: "INDUSTRIAL_PROGRESS",
    displayName: "Steel Milling",
    tier: 2,
    prereqs: ["INDUSTRIAL_BASICS"],
    costTreasury: 1_500,
    costReserves: 200,
    durationMs: 60_000,
    effect: "+10% yield on steel materials.",
  },
  {
    id: "FUEL_REFINING",
    tree: "INDUSTRIAL_PROGRESS",
    displayName: "Fuel Refining",
    tier: 2,
    prereqs: ["INDUSTRIAL_BASICS"],
    costTreasury: 1_500,
    costReserves: 200,
    durationMs: 60_000,
    effect: "+10% yield on fuel materials.",
  },
  {
    id: "PRECISION_OPTICS",
    tree: "INDUSTRIAL_PROGRESS",
    displayName: "Precision Optics",
    tier: 3,
    prereqs: ["STEEL_MILLING"],
    costTreasury: 4_000,
    costReserves: 600,
    durationMs: 120_000,
    effect: "+5% accuracy for AA Base and Air Force units.",
  },
  {
    id: "ELECTRIC_RADIO",
    tree: "INDUSTRIAL_PROGRESS",
    displayName: "Electric Radio",
    tier: 3,
    prereqs: ["FUEL_REFINING"],
    costTreasury: 4_000,
    costReserves: 600,
    durationMs: 120_000,
    effect: "+5% march speed.",
  },
  {
    id: "ASSEMBLY_LINE",
    tree: "INDUSTRIAL_PROGRESS",
    displayName: "Assembly Line",
    tier: 3,
    prereqs: ["STEEL_MILLING", "FUEL_REFINING"],
    costTreasury: 6_000,
    costReserves: 1_000,
    durationMs: 180_000,
    effect: "+1 production slot per Resource Factory and Barracks.",
  },
  {
    id: "ADVANCED_METALLURGY",
    tree: "INDUSTRIAL_PROGRESS",
    displayName: "Advanced Metallurgy",
    tier: 4,
    prereqs: ["PRECISION_OPTICS", "ASSEMBLY_LINE"],
    costTreasury: 12_000,
    costReserves: 2_000,
    durationMs: 360_000,
    effect: "+15% armor on every ground unit.",
  },
  {
    id: "WAR_ECONOMY",
    tree: "INDUSTRIAL_PROGRESS",
    displayName: "War Economy",
    tier: 4,
    prereqs: ["ASSEMBLY_LINE", "ELECTRIC_RADIO"],
    costTreasury: 12_000,
    costReserves: 2_000,
    durationMs: 360_000,
    effect: "+20% treasury yield from all sources.",
  },
];

/** O(1) lookup by node id. */
export const RESEARCH_NODE_BY_ID: Readonly<Record<string, ResearchNode>> = Object.freeze(
  Object.fromEntries(RESEARCH_NODES.map((n) => [n.id, n] as const)),
);

/**
 * Validate the catalog at module load: every prereq must point to an existing
 * node, no node lists itself as a prereq, no cycles. This runs once on import
 * so a malformed catalog crashes the server boot rather than corrupting a live
 * player_research ledger.
 */
function validateCatalog(): void {
  for (const node of RESEARCH_NODES) {
    if (node.prereqs.includes(node.id)) {
      throw new Error(`research: node ${node.id} has itself as a prereq`);
    }
    for (const prereq of node.prereqs) {
      if (!RESEARCH_NODE_BY_ID[prereq]) {
        throw new Error(
          `research: node ${node.id} requires unknown prereq "${prereq}"`,
        );
      }
    }
  }
  // Cycle detection (DFS, returns true if a cycle is found via id).
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const colour: Record<string, number> = {};
  for (const n of RESEARCH_NODES) colour[n.id] = WHITE;
  const visit = (id: string): void => {
    if (colour[id] === GRAY) {
      throw new Error(`research: cycle detected through node ${id}`);
    }
    if (colour[id] === BLACK) return;
    colour[id] = GRAY;
    const node = RESEARCH_NODE_BY_ID[id];
    if (node) {
      for (const p of node.prereqs) visit(p);
    }
    colour[id] = BLACK;
  };
  for (const n of RESEARCH_NODES) visit(n.id);
}
validateCatalog();

/**
 * Per spec §10: "Player has N simultaneous research slots (grows with Research
 * Center level)." Slot growth chosen to reward incremental investment without
 * trivialising the timer constraint.
 */
export function researchSlotCount(researchCenterLevel: number): number {
  if (researchCenterLevel <= 0) return 0;
  if (researchCenterLevel >= 20) return 4;
  if (researchCenterLevel >= 10) return 3;
  if (researchCenterLevel >= 5) return 2;
  return 1;
}

/**
 * Determine the visible state of a node for a given player.
 *
 * - "complete" — present in player_research.
 * - "active" — there is a research_jobs row with state='active' for this node.
 * - "available" — every prereq is complete and the node itself is not active/complete.
 * - "locked" — at least one prereq is missing.
 */
export type ResearchNodeStatus = "locked" | "available" | "active" | "complete";

export function researchNodeStatus(
  nodeId: string,
  completedNodeIds: ReadonlySet<string>,
  activeNodeIds: ReadonlySet<string>,
): ResearchNodeStatus {
  if (completedNodeIds.has(nodeId)) return "complete";
  if (activeNodeIds.has(nodeId)) return "active";
  const node = RESEARCH_NODE_BY_ID[nodeId];
  if (!node) return "locked";
  for (const p of node.prereqs) {
    if (!completedNodeIds.has(p)) return "locked";
  }
  return "available";
}
